import React, { useState, useEffect, useRef } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import "../Dashboard/Dashboard.css";
import { Tooltip } from "antd";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
// import { FiUploadCloud } from "react-icons/fi";
// import { MdDelete } from "react-icons/md";
import { MdKeyboardArrowDown } from "react-icons/md";
import axios from "axios"; // Make sure to install axios: npm install axios
import Loader from "../Loader/Loader";
import useMinimumLoading from "../hooks/Useminimumloading";
import { API_URL } from "../../Config";

// ---------- Date helpers (used by filter + CSV) ----------
const pad2 = (n) => String(n).padStart(2, "0");

// Date -> "yyyy-mm-dd" (local time)
const toInputDate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Booking date string ("dd-mm-yyyy" or "yyyy-mm-dd") -> "yyyy-mm-dd"
const bookingDateToISO = (value) => {
  if (!value) return "";
  const s = String(value).trim().split(/[ T,]/)[0];
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s.split("-").reverse().join("-");
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(value);
  return isNaN(d.getTime()) ? "" : toInputDate(d);
};

// CSV cell escaping
const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const formatCsvDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("en-GB");
};

const GuestRecords = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  // const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    if (savedState !== null) {
      return JSON.parse(savedState);
    }

    return window.innerWidth >= 1024;
  });

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoader = useMinimumLoading(loading);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Date filter state
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeQuick, setActiveQuick] = useState(null);

  // Fetch bookings from API
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/bookings/grouped-by-guest`); // Adjust URL as needed

      if (response.data.success) {
        setBookings(response.data.data);
      } else {
        setError("Failed to fetch bookings");
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError("Error loading bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    setCurrentPage_guest(1);
  }, [searchQuery, dateFrom, dateTo]);
  const statusColors = {
    CANCELLED: "bg-amber-800/20 text-amber-800",
    CONFIRMED: "bg-green-500/20 text-green-500",
    BOOKED: "bg-purple-500/20 text-purple-500",
    Booked: "bg-purple-500/20 text-purple-500",
    FAILED: "bg-red-500/20 text-red-500",
    PENDING: "bg-yellow-200/40 text-yellow-300",
    Pending: "bg-yellow-200/40 text-yellow-300",
    Processing: "bg-orange-300/40 text-orange-400",
    PAID: "bg-transparent text-green-500",
    UNPAID: "bg-transparent text-red-400",
  };

  // ---------- Filter handlers ----------
  const handleQuickFilter = (label) => {
    // clicking the active quick filter again clears it
    if (activeQuick === label) {
      setActiveQuick(null);
      setDateFrom("");
      setDateTo("");
      return;
    }

    const today = new Date();
    const end = toInputDate(today);
    let start = end;

    if (label === "Week") {
      const d = new Date(today);
      d.setDate(d.getDate() - 6); // last 7 days including today
      start = toInputDate(d);
    } else if (label === "Month") {
      start = toInputDate(new Date(today.getFullYear(), today.getMonth(), 1));
    } else if (label === "Year") {
      start = toInputDate(new Date(today.getFullYear(), 0, 1));
    }

    setActiveQuick(label);
    setDateFrom(start);
    setDateTo(end);
  };

  const handleDateFromChange = (e) => {
    setActiveQuick(null);
    setDateFrom(e.target.value);
  };

  const handleDateToChange = (e) => {
    setActiveQuick(null);
    setDateTo(e.target.value);
  };

  // Apply date filter on bookings before grouping
  const filteredBookings =
    dateFrom || dateTo
      ? bookings.filter((b) => {
          const iso = bookingDateToISO(b.date);
          if (!iso) return false;
          if (dateFrom && iso < dateFrom) return false;
          if (dateTo && iso > dateTo) return false;
          return true;
        })
      : bookings;

  // Group bookings by customer (using email and name combination)
  const groupedGuests = Object.values(
    filteredBookings.reduce((acc, booking) => {
      // Create a unique key based on email and name
      const guestKey = `${booking.email}_${booking.customer}`.toLowerCase();

      if (!acc[guestKey]) {
        acc[guestKey] = {
          customer: booking.customer,
          email: booking.email,
          phone: booking.phone,
          bookings: [],
        };
      }
      acc[guestKey].bookings.push(booking);
      return acc;
    }, {}),
  ).map((guest) => ({
    ...guest,
    // Sort bookings by date (newest first)
    bookings: guest.bookings.sort((a, b) => {
      const dateA = new Date(a.date.split("-").reverse().join("-"));
      const dateB = new Date(b.date.split("-").reverse().join("-"));
      return dateB - dateA;
    }),
  }));

  const rowsPerPage_guest = 20;
  const [currentPage_guest, setCurrentPage_guest] = useState(1);

  const indexOfLastTable_guest = currentPage_guest * rowsPerPage_guest;
  const indexOfFirstTable_guest = indexOfLastTable_guest - rowsPerPage_guest;
  const searchedGuests = searchQuery.trim()
    ? groupedGuests.filter((g) => {
        const q = searchQuery.toLowerCase();
        return (
          g.customer?.toLowerCase().includes(q) ||
          g.email?.toLowerCase().includes(q) ||
          g.phone?.toLowerCase().includes(q) ||
          g.bookings.some(
            (b) =>
              b.bookingid?.toString().toLowerCase().includes(q) ||
              b.destination?.toLowerCase().includes(q) ||
              b.property?.toLowerCase().includes(q),
          )
        );
      })
    : groupedGuests;

  const current_guest = searchedGuests.slice(
    indexOfFirstTable_guest,
    indexOfLastTable_guest,
  );

  const totalPages_guest = Math.ceil(searchedGuests.length / rowsPerPage_guest);
  // const current_guest = groupedGuests.slice(
  //   indexOfFirstTable_guest,
  //   indexOfLastTable_guest,
  // );

  // const totalPages_guest = Math.ceil(groupedGuests.length / rowsPerPage_guest);

  const handlePageChange_guest = (pageNumber_guest) => {
    setCurrentPage_guest(pageNumber_guest);
  };

  const handleNextPage_guest = () => {
    if (currentPage_guest < totalPages_guest) {
      setCurrentPage_guest(currentPage_guest + 1);
    }
  };

  const handlePrevPage_guest = () => {
    if (currentPage_guest > 1) {
      setCurrentPage_guest(currentPage_guest - 1);
    }
  };

  const generatePageNumbers_guest = () => {
    const pageNumbers_guest = [];

    if (totalPages_guest <= 3) {
      for (let i = 1; i <= totalPages_guest; i++) {
        pageNumbers_guest.push(i);
      }
    } else {
      if (currentPage_guest > 2) {
        pageNumbers_guest.push(1);
        pageNumbers_guest.push("...");
      }
      for (let i = currentPage_guest - 1; i <= currentPage_guest + 1; i++) {
        if (i > 0 && i <= totalPages_guest) {
          pageNumbers_guest.push(i);
        }
      }
      if (currentPage_guest < totalPages_guest - 1) {
        pageNumbers_guest.push("...");
        pageNumbers_guest.push(totalPages_guest);
      }
    }
    return pageNumbers_guest;
  };

  const [selectedRows_guest, setSelectedRows_guest] = useState([]);

  const handleRowCheckboxChange_guest = (bookingid) => {
    setSelectedRows_guest((prevSelected) =>
      prevSelected.includes(bookingid)
        ? prevSelected.filter((id) => id !== bookingid)
        : [...prevSelected, bookingid],
    );
  };

  const handleSelectAll_guest = () => {
    if (
      selectedRows_guest.length ===
      current_guest.flatMap((g) => g.bookings).length
    ) {
      setSelectedRows_guest([]);
    } else {
      setSelectedRows_guest(
        current_guest.flatMap((g) => g.bookings.map((b) => b.order)),
      );
    }
  };

  // ---------- CSV download ----------
  // Exports selected bookings if any are ticked, otherwise every record
  // matching the current search + date filter (all pages).
  const handleDownloadCSV = () => {
    const allRecords = searchedGuests.flatMap((g) =>
      g.bookings.map((b) => ({ guest: g, booking: b })),
    );

    const records =
      selectedRows_guest.length > 0
        ? allRecords.filter(({ booking }) =>
            selectedRows_guest.includes(booking.order),
          )
        : allRecords;

    if (records.length === 0) return;

    const headers = [
      "Guest Name",
      "Email",
      "Phone",
      "Booking ID",
      "Package / Destination",
      "Property",
      "Check-in",
      "Check-out",
      "Guests",
      "Rooms",
      "Booking Date",
      "Booking Status",
      "Payment Status",
      "Total Amount",
    ];

    const rows = records.map(({ guest, booking }) => [
      booking.customer || guest.customer,
      guest.email,
      guest.phone,
      booking.bookingid ?? booking.order,
      booking.productName || booking.destination,
      booking.property,
      formatCsvDate(booking.checkin),
      formatCsvDate(booking.checkout),
      booking.guests,
      booking.rooms,
      booking.date,
      booking.status,
      booking.paymentstatus,
      booking.payment,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");

    // BOM so Excel opens UTF-8 (e.g. names) correctly
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `guest-records-${toInputDate(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [isOpen_popupForm, setIsOpen_popupForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setIsOpen_popupForm(true);
  };

  // const fileInputRef = useRef(null);

  // const handleBoxClick = () => {
  //   fileInputRef.current.click();
  // };

  const [showPopup_filter, setShowPopup_filter] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowPopup_filter(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [expandedGuest, setExpandedGuest] = useState(null);

  const toggleGuest = (guestEmail) => {
    setExpandedGuest((prev) => (prev === guestEmail ? null : guestEmail));
  };

  // if (showLoader) {
  //   return (
  //     <div className="flex bg-[#F8F9FA]">
  //       <Menubar
  //         isOpen={isSidebarOpen}
  //         setIsOpen={setIsSidebarOpen}
  //         isMobile={isMobile}
  //       />
  //       <main
  //         className={`p-5 lg:p-6 transition-all duration-500 w-full ${isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"}`}
  //       >
  //         {/* <div className="flex justify-center items-center h-64">
  //           <div className="text-gray-500">Loading Records...</div>
  //         </div> */}
  //          <Loader label="Loading..." />
  //       </main>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="flex bg-[#F8F9FA]">
        <Menubar
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          isMobile={isMobile}
        />
        <main
          className={`p-5 lg:p-6 transition-all duration-500 w-full ${isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"}`}
        >
          <div className="flex justify-center items-center h-64">
            <div className="text-red-500">{error}</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`p-5 lg:p-6 transition-all duration-500 w-full ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />

        {/* Header */}
        <div className="flex justify-between gap-5 items-start lg:items-center">
          <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center">
            <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
              Guest Records
            </p>
          </div>

          <div className="flex items-center gap-1">
            <div className="flex items-center">
              <div className="mr-2 p-2 overflow-hidden w-8 h-8 hover:w-[120px] sm:hover:w-[250px] hover:border hover:border-[#1D2826] hover:shadow-[2px_2px_20px_rgba(0,0,0,0.08)] rounded-full flex group items-center hover:duration-300 duration-300">
                <div className="flex items-center justify-center fill-[#1D2826]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="Isolation_Mode"
                    data-name="Isolation Mode"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                  >
                    <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z"></path>
                  </svg>
                </div>
                <input
                  type="text"
                  className="border-none outline-none text-sm bg-transparent w-full font-normal px-2 focus:ring-0"
                  placeholder="Search guests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="">
                <button className="Btn" onClick={handleDownloadCSV}>
                  <svg
                    className="svgIcon"
                    viewBox="0 0 384 512"
                    height="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"></path>
                  </svg>
                  <span className="icon2"></span>
                  <span className="tooltip text-sm">Download</span>
                </button>
              </div>

              <div className="flex items-center">
                <div className="relative inline-block" ref={filterRef}>
                  <div
                    onClick={() => setShowPopup_filter((prev) => !prev)}
                    className={`flex flex-col items-center gap-4 cursor-pointer select-none group scale-[0.75] py-2.5 px-2 rounded-full border-2 transition-all duration-300 ${
                      showPopup_filter
                        ? "border-[#1D2826]"
                        : "border-transparent hover:border-[#1D2826]"
                    }`}
                  >
                    <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full">
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
                        style={{
                          left: showPopup_filter ? "100%" : "0%",
                          transform: `translate(${
                            showPopup_filter ? "-100%" : "0"
                          }, -50%)`,
                        }}
                      ></div>
                    </div>

                    <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full -my-2.5">
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
                        style={{
                          left: showPopup_filter ? "0%" : "100%",
                          transform: `translate(${
                            showPopup_filter ? "0" : "-100%"
                          }, -50%)`,
                        }}
                      ></div>
                    </div>

                    <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full">
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
                        style={{
                          left: showPopup_filter ? "100%" : "0%",
                          transform: `translate(${
                            showPopup_filter ? "-100%" : "0"
                          }, -50%)`,
                        }}
                      ></div>
                    </div>
                  </div>

                  {showPopup_filter && (
                    <div className="absolute top-full mt-1 right-0 bg-white border rounded-lg shadow-custom z-10 p-4 space-y-4">
                      <div className="flex justify-between gap-2">
                        {["Today", "Week", "Month", "Year"].map((label) => (
                          <button
                            key={label}
                            onClick={() => handleQuickFilter(label)}
                            className={`flex-1 py-1 px-2 active:bg-[#DAE0CE] hover:bg-[#DAE0CE] rounded text-sm font-medium ${
                              activeQuick === label
                                ? "bg-[#DAE0CE]"
                                : "bg-gray-100"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={dateFrom}
                            max={dateTo || undefined}
                            onChange={handleDateFromChange}
                            className="border text-sm rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-black"
                          />
                          <span className="text-sm text-gray-500">-</span>
                          <input
                            type="date"
                            value={dateTo}
                            min={dateFrom || undefined}
                            onChange={handleDateToChange}
                            className="border text-sm rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-black"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Button */}
        <div className="flex justify-between gap-10 mt-8">
          {/* <button
            onClick={() => {
              setSelectedBooking(null);
              setIsOpen_popupForm(true);
            }}
            className="px-6 z-30 py-2 bg-[#1D2826] rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-900 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
          >
            + Add Guest
          </button> */}

          <div className="relative z-50">
            {isOpen_popupForm && (
              <div
                onClick={() => setIsOpen_popupForm(false)}
                className="fixed inset-0 bg-black bg-opacity-30 z-40 backdrop_popupForm"
              />
            )}

            <div
              className={`fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg transform transition-transform duration-500 ease-in-out ${
                isOpen_popupForm ? "translate-x-0" : "translate-x-full"
              } panel_popupForm`}
            >
              <div className="p-4 flex justify-between items-center border-b header_popupForm">
                <h2 className="text-[#1D2826] text-lg font-semibold">
                  {selectedBooking ? "View Booking Details" : "Add Guest"}
                </h2>
                <button
                  onClick={() => setIsOpen_popupForm(false)}
                  className="text-gray-500 hover:text-black text-xl"
                >
                  <MdCancel />
                </button>
              </div>

              <div className="p-4">
                <div className="font-semibold text-gray-500 text-sm text-right">
                  Guest ID:{" "}
                  <span className="text-black my-1">
                    {selectedBooking ? selectedBooking.order : "10294837"}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 items-center max-h-[85vh] overflow-y-auto py-2">
                  <div className="flex flex-col w-full">
                    <label
                      htmlFor="input"
                      className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
                    >
                      First Name
                    </label>
                    <input
                      placeholder="Enter first name"
                      name="input"
                      defaultValue={
                        selectedBooking?.customer?.split(" ")[0] || ""
                      }
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label
                      htmlFor="input"
                      className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
                    >
                      Last Name
                    </label>
                    <input
                      placeholder="Enter last name"
                      name="input"
                      defaultValue={
                        selectedBooking?.customer
                          ?.split(" ")
                          .slice(1)
                          .join(" ") || ""
                      }
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label
                      htmlFor="input"
                      className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
                    >
                      Email
                    </label>
                    <input
                      placeholder="Enter your email"
                      name="input"
                      defaultValue={selectedBooking?.email || ""}
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label
                      htmlFor="input"
                      className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
                    >
                      Phone Number
                    </label>
                    <input
                      placeholder="Enter phone number"
                      name="input"
                      defaultValue={selectedBooking?.phone || ""}
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Property
                    </label>
                    <input
                      placeholder="Property"
                      defaultValue={selectedBooking?.property || ""}
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Check-in / Check-out
                    </label>
                    <input
                      placeholder="Check-in / Check-out"
                      defaultValue={
                        selectedBooking
                          ? `${selectedBooking.checkin ? new Date(selectedBooking.checkin).toLocaleDateString("en-GB") : "N/A"} - ${selectedBooking.checkout ? new Date(selectedBooking.checkout).toLocaleDateString("en-GB") : "N/A"}`
                          : ""
                      }
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Guests / Rooms
                    </label>
                    <input
                      placeholder="Guests / Rooms"
                      defaultValue={
                        selectedBooking
                          ? `${selectedBooking.guests || "N/A"} guests / ${selectedBooking.rooms || "N/A"} rooms`
                          : ""
                      }
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Booking Date & Time
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={
                        selectedBooking
                          ? selectedBooking.date
                          : new Date().toLocaleString()
                      }
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md text-black"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Booking Status
                    </label>
                    <input
                      placeholder="Booking status"
                      defaultValue={selectedBooking?.status || ""}
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Payment Status
                    </label>
                    <input
                      placeholder="Payment status"
                      defaultValue={selectedBooking?.paymentstatus || ""}
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="flex flex-col w-full sm:col-span-2">
                    <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
                      Total Amount
                    </label>
                    <input
                      placeholder="Total amount"
                      defaultValue={
                        selectedBooking ? `₹${selectedBooking.payment}` : ""
                      }
                      readOnly={selectedBooking}
                      className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setIsOpen_popupForm(false)}
                      className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
                    >
                      Close
                    </button>

                    {!selectedBooking && (
                      <button className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm">
                        Save
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div>
          <div className="shadow-md rounded-lg mt-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
                  <tr>
                    <th className="p-4">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll_guest}
                        checked={
                          selectedRows_guest.length ===
                            current_guest.flatMap((g) => g.bookings).length &&
                          current_guest.flatMap((g) => g.bookings).length > 0
                        }
                      />
                    </th>
                    <th className="p-4 w-1/10">Guest Name</th>
                    <th className="p-4 w-1/10">Contact Info</th>
                    <th className="p-4 w-1/10">Package / destination</th>
                    <th className="p-4 w-1/10">Booking Date</th>
                    <th className="p-4 w-1/12">Status</th>
                    <th className="p-4 w-1/10">Payment</th>
                    <th className="p-4 w-10">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {showLoader ? (
                    <tr>
                      <td colSpan={9}>
                        <Loader
                          label="Loading..."
                          fullScreen={false}
                          className="py-2"
                        />
                      </td>
                    </tr>
                  ) : current_guest.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="text-center py-14 text-gray-400"
                      >
                        No guests found.
                      </td>
                    </tr>
                  ) : (
                    current_guest.map((guest, index) => {
                      const latest = guest.bookings[0];
                      const guestKey =
                        `${guest.email}_${guest.customer}`.toLowerCase();
                      const isExpanded = expandedGuest === guestKey;

                      return (
                        <React.Fragment key={guestKey}>
                          {/* MAIN ROW */}
                          <tr
                            className="bg-white border-b hover:bg-gray-100 text-gray-700 cursor-pointer"
                            onClick={() => toggleGuest(guestKey)}
                          >
                            {/* CHECKBOX */}
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                onClick={(e) => e.stopPropagation()}
                                onChange={() =>
                                  handleRowCheckboxChange_guest(latest.order)
                                }
                                checked={selectedRows_guest.includes(
                                  latest.order,
                                )}
                              />
                            </td>

                            {/* CUSTOMER */}
                            <td className="px-4 py-3 font-semibold">
                              <Tooltip title={guest.customer} placement="left">
                                <span>{guest.customer}</span>
                              </Tooltip>
                              {guest.bookings.length > 1 && (
                                <span className="text-[10px] text-gray-400 ml-1">
                                  ({guest.bookings.length} bookings)
                                </span>
                              )}
                            </td>

                            {/* CONTACT INFO */}
                            <td className="px-4 py-3">
                              <div className="flex flex-col">
                                <span className="text-xs">{guest.email}</span>
                                <span className="text-xs text-gray-400">
                                  {guest.phone}
                                </span>
                              </div>
                            </td>

                            {/* PRODUCT */}
                            <td className="px-4 py-3">
                              <Tooltip
                                title={latest.productName}
                                placement="left"
                              >
                                <span>{latest.productName}</span>
                              </Tooltip>
                            </td>

                            {/* DATE */}
                            <td className="px-4 py-3">
                              <Tooltip title={latest.date} placement="left">
                                <span>{latest.date}</span>
                              </Tooltip>
                            </td>

                            {/* STATUS */}
                            <td className="px-4 py-3">
                              <button
                                className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 cursor-default ${statusColors[latest.status] || "bg-gray-200 text-gray-600"}`}
                              >
                                {latest.status}
                              </button>
                            </td>

                            {/* PAYMENT */}
                            <td className="px-4 py-3">
                              <Tooltip
                                title={`₹${latest.payment}`}
                                placement="left"
                              >
                                <span>₹{latest.payment}</span>
                              </Tooltip>
                            </td>

                            {/* ACTIONS */}
                            <td className="pr-4 py-3">
                              <div className="flex justify-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewBooking(latest);
                                  }}
                                  className="px-2 py-1 text-gray-400 hover:text-black hover:scale-125 transition-all"
                                >
                                  <FaEye size={15} />
                                </button>

                                {/* <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewBooking(latest);
                                }}
                                className="px-2 py-1 text-gray-400 hover:text-black hover:scale-125 transition-all"
                              >
                                <FaEdit size={14} />
                              </button> */}
                                {/* 
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle delete
                                }}
                                className="px-2 py-1 text-gray-400 hover:text-black hover:scale-125 transition-all"
                              >
                                <MdDelete size={15} />
                              </button> */}
                              </div>
                            </td>

                            {/* ARROW */}
                            <td className="px-0 pt-2.5 text-black">
                              {guest.bookings.length > 1 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGuest(guestKey);
                                  }}
                                >
                                  <MdKeyboardArrowDown
                                    size={20}
                                    className={`transition-transform duration-300 ${
                                      isExpanded ? "rotate-180" : "rotate-0"
                                    }`}
                                  />
                                </button>
                              )}
                            </td>
                          </tr>

                          {/* DROPDOWN ROWS */}
                          {isExpanded &&
                            guest.bookings.slice(1).map((booking, i) => (
                              <tr
                                key={`${booking.order}-${i}`}
                                className="bg-gray-50 border-b text-gray-700"
                              >
                                <td className="px-4 py-3">
                                  <input
                                    type="checkbox"
                                    onChange={() =>
                                      handleRowCheckboxChange_guest(
                                        booking.order,
                                      )
                                    }
                                    checked={selectedRows_guest.includes(
                                      booking.order,
                                    )}
                                  />
                                </td>
                                <td className="px-4 py-3 pl-4">
                                  <Tooltip
                                    title={booking.customer}
                                    placement="left"
                                  >
                                    <span>{booking.customer}</span>
                                  </Tooltip>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-col">
                                    <span className="text-xs">
                                      {guest.email}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {guest.phone}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <Tooltip
                                    title={booking.productName}
                                    placement="left"
                                  >
                                    <span>{booking.productName}</span>
                                  </Tooltip>
                                </td>
                                <td className="px-4 py-3">{booking.date}</td>
                                <td className="px-4 py-3">
                                  <button
                                    className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 cursor-default ${statusColors[booking.status] || "bg-gray-200 text-gray-600"}`}
                                  >
                                    {booking.status}
                                  </button>
                                </td>

                                <td className="px-4 py-3">
                                  ₹{booking.payment}
                                </td>
                                <td className="pr-4 py-3">
                                  <div className="flex justify-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewBooking(booking);
                                      }}
                                      className="px-2 text-gray-400 hover:text-black"
                                    >
                                      <FaEye size={15} />
                                    </button>
                                  </div>

                                  {/* <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewBooking(booking);
                                  }}
                                  className="px-2 text-gray-400 hover:text-black"
                                >
                                  <FaEdit size={14} />
                                </button>

                                <button className="px-2 text-gray-400 hover:text-black">
                                  <MdDelete size={15} />
                                </button> */}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <nav
              className="flex items-center flex-column flex-wrap md:flex-row justify-between rounded-b-lg px-2 py-1 bg-[#f7f7f7]"
              aria-label="Table navigation"
            >
              <span className="text-xs font-normal text-gray-500 mb-4 md:mb-0 block w-full md:inline md:w-auto">
                Showing{" "}
                <span className="font-semibold text-gray-700 dark:text-white">
                  {searchedGuests.length === 0
                    ? 0
                    : indexOfFirstTable_guest + 1}
                  -
                  {Math.min(indexOfLastTable_guest, searchedGuests.length)}{" "}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-700 dark:text-white">
                  {searchedGuests.length}
                </span>{" "}
                guests
              </span>

              <ul className="inline-flex -space-x-px rtl:space-x-reverse text-xs h-8">
                <li>
                  <button
                    onClick={handlePrevPage_guest}
                    disabled={currentPage_guest === 1}
                    className="flex items-center justify-center px-1 h-8 ms-0 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                  >
                    <MdKeyboardDoubleArrowLeft />
                  </button>
                </li>
                {generatePageNumbers_guest().map((page, index) =>
                  page === "..." ? (
                    <li
                      key={index}
                      className="px-1 h-8 flex items-center justify-center text-gray-500 bg-[#f7f7f7] border-gray-300 dark:text-gray-400 dark:bg-gray-800 dark:border-gray-700"
                    >
                      <span>...</span>
                    </li>
                  ) : (
                    <li key={index}>
                      <button
                        onClick={() => handlePageChange_guest(page)}
                        className={`flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${
                          currentPage_guest === page
                            ? "text-green-500 underline underline-offset-2"
                            : ""
                        }`}
                      >
                        {page}
                      </button>
                    </li>
                  ),
                )}
                <li>
                  <button
                    onClick={handleNextPage_guest}
                    disabled={currentPage_guest === totalPages_guest}
                    className="flex items-center justify-center px-1 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                  >
                    <MdKeyboardDoubleArrowRight />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GuestRecords;
//Working code for GuestRecords.jsx
// import React, { useState, useEffect, useRef } from "react";
// import Menubar from "../Menubar/Menubar";
// import { Outlet } from "react-router-dom";
// import "../Dashboard/Dashboard.css";
// import { Tooltip } from "antd";
// import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
// import { MdKeyboardDoubleArrowRight } from "react-icons/md";
// import { FaEye } from "react-icons/fa";
// import { MdCancel } from "react-icons/md";
// // import { FiUploadCloud } from "react-icons/fi";
// // import { MdDelete } from "react-icons/md";
// import { MdKeyboardArrowDown } from "react-icons/md";
// import axios from "axios"; // Make sure to install axios: npm install axios
// import Loader from "../Loader/Loader";
// import useMinimumLoading from "../hooks/Useminimumloading";
// import { API_URL } from "../../Config";

// const GuestRecords = () => {
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   // const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const savedState = localStorage.getItem("menubarOpen");

//     if (savedState !== null) {
//       return JSON.parse(savedState);
//     }

//     return window.innerWidth >= 1024;
//   });

//   const [bookings, setBookings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const showLoader = useMinimumLoading(loading);
//   const [error, setError] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   // Fetch bookings from API
//   useEffect(() => {
//     fetchBookings();
//   }, []);

//   const fetchBookings = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.get(`${API_URL}/bookings/grouped-by-guest`); // Adjust URL as needed

//       if (response.data.success) {
//         setBookings(response.data.data);
//       } else {
//         setError("Failed to fetch bookings");
//       }
//     } catch (err) {
//       console.error("Error fetching bookings:", err);
//       setError("Error loading bookings");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);

//       if (mobile) {
//         setIsSidebarOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);
//   useEffect(() => {
//     setCurrentPage_guest(1);
//   }, [searchQuery]);
//   const statusColors = {
//     CANCELLED: "bg-amber-800/20 text-amber-800",
//     CONFIRMED: "bg-green-500/20 text-green-500",
//     BOOKED: "bg-purple-500/20 text-purple-500",
//     Booked: "bg-purple-500/20 text-purple-500",
//     FAILED: "bg-red-500/20 text-red-500",
//     PENDING: "bg-yellow-200/40 text-yellow-300",
//     Pending: "bg-yellow-200/40 text-yellow-300",
//     Processing: "bg-orange-300/40 text-orange-400",
//     PAID: "bg-transparent text-green-500",
//     UNPAID: "bg-transparent text-red-400",
//   };

//   // Group bookings by customer (using email and name combination)
//   const groupedGuests = Object.values(
//     bookings.reduce((acc, booking) => {
//       // Create a unique key based on email and name
//       const guestKey = `${booking.email}_${booking.customer}`.toLowerCase();

//       if (!acc[guestKey]) {
//         acc[guestKey] = {
//           customer: booking.customer,
//           email: booking.email,
//           phone: booking.phone,
//           bookings: [],
//         };
//       }
//       acc[guestKey].bookings.push(booking);
//       return acc;
//     }, {}),
//   ).map((guest) => ({
//     ...guest,
//     // Sort bookings by date (newest first)
//     bookings: guest.bookings.sort((a, b) => {
//       const dateA = new Date(a.date.split("-").reverse().join("-"));
//       const dateB = new Date(b.date.split("-").reverse().join("-"));
//       return dateB - dateA;
//     }),
//   }));

//   const rowsPerPage_guest = 20;
//   const [currentPage_guest, setCurrentPage_guest] = useState(1);

//   const indexOfLastTable_guest = currentPage_guest * rowsPerPage_guest;
//   const indexOfFirstTable_guest = indexOfLastTable_guest - rowsPerPage_guest;
//   const searchedGuests = searchQuery.trim()
//     ? groupedGuests.filter((g) => {
//         const q = searchQuery.toLowerCase();
//         return (
//           g.customer?.toLowerCase().includes(q) ||
//           g.email?.toLowerCase().includes(q) ||
//           g.phone?.toLowerCase().includes(q) ||
//           g.bookings.some(
//             (b) =>
//               b.bookingid?.toString().toLowerCase().includes(q) ||
//               b.destination?.toLowerCase().includes(q) ||
//               b.property?.toLowerCase().includes(q),
//           )
//         );
//       })
//     : groupedGuests;

//   const current_guest = searchedGuests.slice(
//     indexOfFirstTable_guest,
//     indexOfLastTable_guest,
//   );

//   const totalPages_guest = Math.ceil(searchedGuests.length / rowsPerPage_guest);
//   // const current_guest = groupedGuests.slice(
//   //   indexOfFirstTable_guest,
//   //   indexOfLastTable_guest,
//   // );

//   // const totalPages_guest = Math.ceil(groupedGuests.length / rowsPerPage_guest);

//   const handlePageChange_guest = (pageNumber_guest) => {
//     setCurrentPage_guest(pageNumber_guest);
//   };

//   const handleNextPage_guest = () => {
//     if (currentPage_guest < totalPages_guest) {
//       setCurrentPage_guest(currentPage_guest + 1);
//     }
//   };

//   const handlePrevPage_guest = () => {
//     if (currentPage_guest > 1) {
//       setCurrentPage_guest(currentPage_guest - 1);
//     }
//   };

//   const generatePageNumbers_guest = () => {
//     const pageNumbers_guest = [];

//     if (totalPages_guest <= 3) {
//       for (let i = 1; i <= totalPages_guest; i++) {
//         pageNumbers_guest.push(i);
//       }
//     } else {
//       if (currentPage_guest > 2) {
//         pageNumbers_guest.push(1);
//         pageNumbers_guest.push("...");
//       }
//       for (let i = currentPage_guest - 1; i <= currentPage_guest + 1; i++) {
//         if (i > 0 && i <= totalPages_guest) {
//           pageNumbers_guest.push(i);
//         }
//       }
//       if (currentPage_guest < totalPages_guest - 1) {
//         pageNumbers_guest.push("...");
//         pageNumbers_guest.push(totalPages_guest);
//       }
//     }
//     return pageNumbers_guest;
//   };

//   const [selectedRows_guest, setSelectedRows_guest] = useState([]);

//   const handleRowCheckboxChange_guest = (bookingid) => {
//     setSelectedRows_guest((prevSelected) =>
//       prevSelected.includes(bookingid)
//         ? prevSelected.filter((id) => id !== bookingid)
//         : [...prevSelected, bookingid],
//     );
//   };

//   const handleSelectAll_guest = () => {
//     if (
//       selectedRows_guest.length ===
//       current_guest.flatMap((g) => g.bookings).length
//     ) {
//       setSelectedRows_guest([]);
//     } else {
//       setSelectedRows_guest(
//         current_guest.flatMap((g) => g.bookings.map((b) => b.order)),
//       );
//     }
//   };

//   const [isOpen_popupForm, setIsOpen_popupForm] = useState(false);
//   const [selectedBooking, setSelectedBooking] = useState(null);

//   const handleViewBooking = (booking) => {
//     setSelectedBooking(booking);
//     setIsOpen_popupForm(true);
//   };

//   // const fileInputRef = useRef(null);

//   // const handleBoxClick = () => {
//   //   fileInputRef.current.click();
//   // };

//   const [showPopup_filter, setShowPopup_filter] = useState(false);
//   const filterRef = useRef(null);

//   useEffect(() => {
//     function handleClickOutside(event) {
//       if (filterRef.current && !filterRef.current.contains(event.target)) {
//         setShowPopup_filter(false);
//       }
//     }

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const [expandedGuest, setExpandedGuest] = useState(null);

//   const toggleGuest = (guestEmail) => {
//     setExpandedGuest((prev) => (prev === guestEmail ? null : guestEmail));
//   };

//   // if (showLoader) {
//   //   return (
//   //     <div className="flex bg-[#F8F9FA]">
//   //       <Menubar
//   //         isOpen={isSidebarOpen}
//   //         setIsOpen={setIsSidebarOpen}
//   //         isMobile={isMobile}
//   //       />
//   //       <main
//   //         className={`p-5 lg:p-6 transition-all duration-500 w-full ${isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"}`}
//   //       >
//   //         {/* <div className="flex justify-center items-center h-64">
//   //           <div className="text-gray-500">Loading Records...</div>
//   //         </div> */}
//   //          <Loader label="Loading..." />
//   //       </main>
//   //     </div>
//   //   );
//   // }

//   if (error) {
//     return (
//       <div className="flex bg-[#F8F9FA]">
//         <Menubar
//           isOpen={isSidebarOpen}
//           setIsOpen={setIsSidebarOpen}
//           isMobile={isMobile}
//         />
//         <main
//           className={`p-5 lg:p-6 transition-all duration-500 w-full ${isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"}`}
//         >
//           <div className="flex justify-center items-center h-64">
//             <div className="text-red-500">{error}</div>
//           </div>
//         </main>
//       </div>
//     );
//   }

//   return (
//     <div className="flex bg-[#F8F9FA]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`p-5 lg:p-6 transition-all duration-500 w-full ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <Outlet />

//         {/* Header */}
//         <div className="flex justify-between gap-5 items-start lg:items-center">
//           <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center">
//             <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
//               Guest Records
//             </p>
//           </div>

//           <div className="flex items-center gap-1">
//             <div className="flex items-center">
//               <div className="mr-2 p-2 overflow-hidden w-8 h-8 hover:w-[120px] sm:hover:w-[250px] hover:border hover:border-[#1D2826] hover:shadow-[2px_2px_20px_rgba(0,0,0,0.08)] rounded-full flex group items-center hover:duration-300 duration-300">
//                 <div className="flex items-center justify-center fill-[#1D2826]">
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     id="Isolation_Mode"
//                     data-name="Isolation Mode"
//                     viewBox="0 0 24 24"
//                     width="18"
//                     height="18"
//                   >
//                     <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z"></path>
//                   </svg>
//                 </div>
//                 <input
//                   type="text"
//                   className="border-none outline-none text-sm bg-transparent w-full font-normal px-2 focus:ring-0"
//                   placeholder="Search guests..."
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                 />
//               </div>

//               <div className="">
//                 <button className="Btn">
//                   <svg
//                     className="svgIcon"
//                     viewBox="0 0 384 512"
//                     height="1em"
//                     xmlns="http://www.w3.org/2000/svg"
//                   >
//                     <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"></path>
//                   </svg>
//                   <span className="icon2"></span>
//                   <span className="tooltip text-sm">Download</span>
//                 </button>
//               </div>

//               <div className="flex items-center">
//                 <div className="relative inline-block" ref={filterRef}>
//                   <div
//                     onClick={() => setShowPopup_filter((prev) => !prev)}
//                     className={`flex flex-col items-center gap-4 cursor-pointer select-none group scale-[0.75] py-2.5 px-2 rounded-full border-2 transition-all duration-300 ${
//                       showPopup_filter
//                         ? "border-[#1D2826]"
//                         : "border-transparent hover:border-[#1D2826]"
//                     }`}
//                   >
//                     <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full">
//                       <div
//                         className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
//                         style={{
//                           left: showPopup_filter ? "100%" : "0%",
//                           transform: `translate(${
//                             showPopup_filter ? "-100%" : "0"
//                           }, -50%)`,
//                         }}
//                       ></div>
//                     </div>

//                     <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full -my-2.5">
//                       <div
//                         className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
//                         style={{
//                           left: showPopup_filter ? "0%" : "100%",
//                           transform: `translate(${
//                             showPopup_filter ? "0" : "-100%"
//                           }, -50%)`,
//                         }}
//                       ></div>
//                     </div>

//                     <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full">
//                       <div
//                         className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
//                         style={{
//                           left: showPopup_filter ? "100%" : "0%",
//                           transform: `translate(${
//                             showPopup_filter ? "-100%" : "0"
//                           }, -50%)`,
//                         }}
//                       ></div>
//                     </div>
//                   </div>

//                   {showPopup_filter && (
//                     <div className="absolute top-full mt-1 right-0 bg-white border rounded-lg shadow-custom z-10 p-4 space-y-4">
//                       <div className="flex justify-between gap-2">
//                         {["Today", "Week", "Month", "Year"].map((label) => (
//                           <button
//                             key={label}
//                             className="flex-1 py-1 px-2 bg-gray-100 active:bg-[#DAE0CE] hover:bg-[#DAE0CE] rounded text-sm font-medium"
//                           >
//                             {label}
//                           </button>
//                         ))}
//                       </div>

//                       <div className="flex flex-col gap-2">
//                         <div className="flex items-center gap-2">
//                           <input
//                             type="date"
//                             className="border text-sm rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-black"
//                           />
//                           <span className="text-sm text-gray-500">-</span>
//                           <input
//                             type="date"
//                             className="border text-sm rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-black"
//                           />
//                         </div>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Button */}
//         <div className="flex justify-between gap-10 mt-8">
//           {/* <button
//             onClick={() => {
//               setSelectedBooking(null);
//               setIsOpen_popupForm(true);
//             }}
//             className="px-6 z-30 py-2 bg-[#1D2826] rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-900 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//           >
//             + Add Guest
//           </button> */}

//           <div className="relative z-50">
//             {isOpen_popupForm && (
//               <div
//                 onClick={() => setIsOpen_popupForm(false)}
//                 className="fixed inset-0 bg-black bg-opacity-30 z-40 backdrop_popupForm"
//               />
//             )}

//             <div
//               className={`fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg transform transition-transform duration-500 ease-in-out ${
//                 isOpen_popupForm ? "translate-x-0" : "translate-x-full"
//               } panel_popupForm`}
//             >
//               <div className="p-4 flex justify-between items-center border-b header_popupForm">
//                 <h2 className="text-[#1D2826] text-lg font-semibold">
//                   {selectedBooking ? "View Booking Details" : "Add Guest"}
//                 </h2>
//                 <button
//                   onClick={() => setIsOpen_popupForm(false)}
//                   className="text-gray-500 hover:text-black text-xl"
//                 >
//                   <MdCancel />
//                 </button>
//               </div>

//               <div className="p-4">
//                 <div className="font-semibold text-gray-500 text-sm text-right">
//                   Guest ID:{" "}
//                   <span className="text-black my-1">
//                     {selectedBooking ? selectedBooking.order : "10294837"}
//                   </span>
//                 </div>

//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 items-center max-h-[85vh] overflow-y-auto py-2">
//                   <div className="flex flex-col w-full">
//                     <label
//                       htmlFor="input"
//                       className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                     >
//                       First Name
//                     </label>
//                     <input
//                       placeholder="Enter first name"
//                       name="input"
//                       defaultValue={
//                         selectedBooking?.customer?.split(" ")[0] || ""
//                       }
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label
//                       htmlFor="input"
//                       className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                     >
//                       Last Name
//                     </label>
//                     <input
//                       placeholder="Enter last name"
//                       name="input"
//                       defaultValue={
//                         selectedBooking?.customer
//                           ?.split(" ")
//                           .slice(1)
//                           .join(" ") || ""
//                       }
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label
//                       htmlFor="input"
//                       className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                     >
//                       Email
//                     </label>
//                     <input
//                       placeholder="Enter your email"
//                       name="input"
//                       defaultValue={selectedBooking?.email || ""}
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label
//                       htmlFor="input"
//                       className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit"
//                     >
//                       Phone Number
//                     </label>
//                     <input
//                       placeholder="Enter phone number"
//                       name="input"
//                       defaultValue={selectedBooking?.phone || ""}
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Property
//                     </label>
//                     <input
//                       placeholder="Property"
//                       defaultValue={selectedBooking?.property || ""}
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Check-in / Check-out
//                     </label>
//                     <input
//                       placeholder="Check-in / Check-out"
//                       defaultValue={
//                         selectedBooking
//                           ? `${selectedBooking.checkin ? new Date(selectedBooking.checkin).toLocaleDateString("en-GB") : "N/A"} - ${selectedBooking.checkout ? new Date(selectedBooking.checkout).toLocaleDateString("en-GB") : "N/A"}`
//                           : ""
//                       }
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Guests / Rooms
//                     </label>
//                     <input
//                       placeholder="Guests / Rooms"
//                       defaultValue={
//                         selectedBooking
//                           ? `${selectedBooking.guests || "N/A"} guests / ${selectedBooking.rooms || "N/A"} rooms`
//                           : ""
//                       }
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Booking Date & Time
//                     </label>
//                     <input
//                       type="text"
//                       readOnly
//                       value={
//                         selectedBooking
//                           ? selectedBooking.date
//                           : new Date().toLocaleString()
//                       }
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md text-black"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Booking Status
//                     </label>
//                     <input
//                       placeholder="Booking status"
//                       defaultValue={selectedBooking?.status || ""}
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Payment Status
//                     </label>
//                     <input
//                       placeholder="Payment status"
//                       defaultValue={selectedBooking?.paymentstatus || ""}
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="flex flex-col w-full sm:col-span-2">
//                     <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-white w-fit">
//                       Total Amount
//                     </label>
//                     <input
//                       placeholder="Total amount"
//                       defaultValue={
//                         selectedBooking ? `₹${selectedBooking.payment}` : ""
//                       }
//                       readOnly={selectedBooking}
//                       className="border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                     />
//                   </div>

//                   <div className="grid grid-cols-2 gap-3">
//                     <button
//                       onClick={() => setIsOpen_popupForm(false)}
//                       className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//                     >
//                       Close
//                     </button>

//                     {!selectedBooking && (
//                       <button className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm">
//                         Save
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Table */}
//         <div>
//           <div className="shadow-md rounded-lg mt-5">
//             <div className="overflow-x-auto">
//               <table className="w-full text-sm text-left rtl:text-right text-gray-500">
//                 <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
//                   <tr>
//                     <th className="p-4">
//                       <input
//                         type="checkbox"
//                         onChange={handleSelectAll_guest}
//                         checked={
//                           selectedRows_guest.length ===
//                             current_guest.flatMap((g) => g.bookings).length &&
//                           current_guest.flatMap((g) => g.bookings).length > 0
//                         }
//                       />
//                     </th>
//                     <th className="p-4 w-1/10">Guest Name</th>
//                     <th className="p-4 w-1/10">Contact Info</th>
//                     <th className="p-4 w-1/10">Package / destination</th>
//                     <th className="p-4 w-1/10">Booking Date</th>
//                     <th className="p-4 w-1/12">Status</th>
//                     <th className="p-4 w-1/10">Payment</th>
//                     <th className="p-4 w-10">Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {showLoader ? (
//                     <tr>
//                       <td colSpan={9}>
//                         <Loader
//                           label="Loading..."
//                           fullScreen={false}
//                           className="py-2"
//                         />
//                       </td>
//                     </tr>
//                   ) : current_guest.length === 0 ? (
//                     <tr>
//                       <td
//                         colSpan={9}
//                         className="text-center py-14 text-gray-400"
//                       >
//                         No guests found.
//                       </td>
//                     </tr>
//                   ) : (
//                     current_guest.map((guest, index) => {
//                       const latest = guest.bookings[0];
//                       const guestKey =
//                         `${guest.email}_${guest.customer}`.toLowerCase();
//                       const isExpanded = expandedGuest === guestKey;

//                       return (
//                         <React.Fragment key={guestKey}>
//                           {/* MAIN ROW */}
//                           <tr
//                             className="bg-white border-b hover:bg-gray-100 text-gray-700 cursor-pointer"
//                             onClick={() => toggleGuest(guestKey)}
//                           >
//                             {/* CHECKBOX */}
//                             <td className="px-4 py-3">
//                               <input
//                                 type="checkbox"
//                                 onClick={(e) => e.stopPropagation()}
//                                 onChange={() =>
//                                   handleRowCheckboxChange_guest(latest.order)
//                                 }
//                                 checked={selectedRows_guest.includes(
//                                   latest.order,
//                                 )}
//                               />
//                             </td>

//                             {/* CUSTOMER */}
//                             <td className="px-4 py-3 font-semibold">
//                               <Tooltip title={guest.customer} placement="left">
//                                 <span>{guest.customer}</span>
//                               </Tooltip>
//                               {guest.bookings.length > 1 && (
//                                 <span className="text-[10px] text-gray-400 ml-1">
//                                   ({guest.bookings.length} bookings)
//                                 </span>
//                               )}
//                             </td>

//                             {/* CONTACT INFO */}
//                             <td className="px-4 py-3">
//                               <div className="flex flex-col">
//                                 <span className="text-xs">{guest.email}</span>
//                                 <span className="text-xs text-gray-400">
//                                   {guest.phone}
//                                 </span>
//                               </div>
//                             </td>

//                             {/* PRODUCT */}
//                             <td className="px-4 py-3">
//                               <Tooltip
//                                 title={latest.productName}
//                                 placement="left"
//                               >
//                                 <span>{latest.productName}</span>
//                               </Tooltip>
//                             </td>

//                             {/* DATE */}
//                             <td className="px-4 py-3">
//                               <Tooltip title={latest.date} placement="left">
//                                 <span>{latest.date}</span>
//                               </Tooltip>
//                             </td>

//                             {/* STATUS */}
//                             <td className="px-4 py-3">
//                               <button
//                                 className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 cursor-default ${statusColors[latest.status] || "bg-gray-200 text-gray-600"}`}
//                               >
//                                 {latest.status}
//                               </button>
//                             </td>

//                             {/* PAYMENT */}
//                             <td className="px-4 py-3">
//                               <Tooltip
//                                 title={`₹${latest.payment}`}
//                                 placement="left"
//                               >
//                                 <span>₹{latest.payment}</span>
//                               </Tooltip>
//                             </td>

//                             {/* ACTIONS */}
//                             <td className="pr-4 py-3">
//                               <div className="flex justify-center">
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleViewBooking(latest);
//                                   }}
//                                   className="px-2 py-1 text-gray-400 hover:text-black hover:scale-125 transition-all"
//                                 >
//                                   <FaEye size={15} />
//                                 </button>

//                                 {/* <button
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handleViewBooking(latest);
//                                 }}
//                                 className="px-2 py-1 text-gray-400 hover:text-black hover:scale-125 transition-all"
//                               >
//                                 <FaEdit size={14} />
//                               </button> */}
//                                 {/*
//                               <button
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   // Handle delete
//                                 }}
//                                 className="px-2 py-1 text-gray-400 hover:text-black hover:scale-125 transition-all"
//                               >
//                                 <MdDelete size={15} />
//                               </button> */}
//                               </div>
//                             </td>

//                             {/* ARROW */}
//                             <td className="px-0 pt-2.5 text-black">
//                               {guest.bookings.length > 1 && (
//                                 <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     toggleGuest(guestKey);
//                                   }}
//                                 >
//                                   <MdKeyboardArrowDown
//                                     size={20}
//                                     className={`transition-transform duration-300 ${
//                                       isExpanded ? "rotate-180" : "rotate-0"
//                                     }`}
//                                   />
//                                 </button>
//                               )}
//                             </td>
//                           </tr>

//                           {/* DROPDOWN ROWS */}
//                           {isExpanded &&
//                             guest.bookings.slice(1).map((booking, i) => (
//                               <tr
//                                 key={`${booking.order}-${i}`}
//                                 className="bg-gray-50 border-b text-gray-700"
//                               >
//                                 <td className="px-4 py-3">
//                                   <input
//                                     type="checkbox"
//                                     onChange={() =>
//                                       handleRowCheckboxChange_guest(
//                                         booking.order,
//                                       )
//                                     }
//                                     checked={selectedRows_guest.includes(
//                                       booking.order,
//                                     )}
//                                   />
//                                 </td>
//                                 <td className="px-4 py-3 pl-4">
//                                   <Tooltip
//                                     title={booking.customer}
//                                     placement="left"
//                                   >
//                                     <span>{booking.customer}</span>
//                                   </Tooltip>
//                                 </td>
//                                 <td className="px-4 py-3">
//                                   <div className="flex flex-col">
//                                     <span className="text-xs">
//                                       {guest.email}
//                                     </span>
//                                     <span className="text-xs text-gray-400">
//                                       {guest.phone}
//                                     </span>
//                                   </div>
//                                 </td>
//                                 <td className="px-4 py-3">
//                                   <Tooltip
//                                     title={booking.productName}
//                                     placement="left"
//                                   >
//                                     <span>{booking.productName}</span>
//                                   </Tooltip>
//                                 </td>
//                                 <td className="px-4 py-3">{booking.date}</td>
//                                 <td className="px-4 py-3">
//                                   <button
//                                     className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 cursor-default ${statusColors[booking.status] || "bg-gray-200 text-gray-600"}`}
//                                   >
//                                     {booking.status}
//                                   </button>
//                                 </td>

//                                 <td className="px-4 py-3">
//                                   ₹{booking.payment}
//                                 </td>
//                                 <td className="pr-4 py-3">
//                                   <div className="flex justify-center">
//                                     <button
//                                       onClick={(e) => {
//                                         e.stopPropagation();
//                                         handleViewBooking(booking);
//                                       }}
//                                       className="px-2 text-gray-400 hover:text-black"
//                                     >
//                                       <FaEye size={15} />
//                                     </button>
//                                   </div>

//                                   {/* <button
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     handleViewBooking(booking);
//                                   }}
//                                   className="px-2 text-gray-400 hover:text-black"
//                                 >
//                                   <FaEdit size={14} />
//                                 </button>

//                                 <button className="px-2 text-gray-400 hover:text-black">
//                                   <MdDelete size={15} />
//                                 </button> */}
//                                 </td>
//                               </tr>
//                             ))}
//                         </React.Fragment>
//                       );
//                     })
//                   )}
//                 </tbody>
//               </table>
//             </div>

//             <nav
//               className="flex items-center flex-column flex-wrap md:flex-row justify-between rounded-b-lg px-2 py-1 bg-[#f7f7f7]"
//               aria-label="Table navigation"
//             >
//               <span className="text-xs font-normal text-gray-500 mb-4 md:mb-0 block w-full md:inline md:w-auto">
//                 Showing{" "}
//                 <span className="font-semibold text-gray-700 dark:text-white">
//                   {indexOfFirstTable_guest + 1}-
//                   {Math.min(indexOfLastTable_guest, groupedGuests.length)}{" "}
//                 </span>{" "}
//                 of{" "}
//                 <span className="font-semibold text-gray-700 dark:text-white">
//                   {groupedGuests.length}
//                 </span>{" "}
//                 guests
//               </span>

//               <ul className="inline-flex -space-x-px rtl:space-x-reverse text-xs h-8">
//                 <li>
//                   <button
//                     onClick={handlePrevPage_guest}
//                     disabled={currentPage_guest === 1}
//                     className="flex items-center justify-center px-1 h-8 ms-0 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
//                   >
//                     <MdKeyboardDoubleArrowLeft />
//                   </button>
//                 </li>
//                 {generatePageNumbers_guest().map((page, index) =>
//                   page === "..." ? (
//                     <li
//                       key={index}
//                       className="px-1 h-8 flex items-center justify-center text-gray-500 bg-[#f7f7f7] border-gray-300 dark:text-gray-400 dark:bg-gray-800 dark:border-gray-700"
//                     >
//                       <span>...</span>
//                     </li>
//                   ) : (
//                     <li key={index}>
//                       <button
//                         onClick={() => handlePageChange_guest(page)}
//                         className={`flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${
//                           currentPage_guest === page
//                             ? "text-green-500 underline underline-offset-2"
//                             : ""
//                         }`}
//                       >
//                         {page}
//                       </button>
//                     </li>
//                   ),
//                 )}
//                 <li>
//                   <button
//                     onClick={handleNextPage_guest}
//                     disabled={currentPage_guest === totalPages_guest}
//                     className="flex items-center justify-center px-1 h-8 leading-tight text-gray-500 bg-[#f7f7f7] border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
//                   >
//                     <MdKeyboardDoubleArrowRight />
//                   </button>
//                 </li>
//               </ul>
//             </nav>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default GuestRecords;
